import { STORAGE_KEYS } from './constants';
import type { StoredBid } from '@/types';

export type Theme = 'dark' | 'light';

/**
 * Get the stored theme preference
 */
export function getStoredTheme(): Theme | null {
  return localStorage.getItem(STORAGE_KEYS.THEME) as Theme | null;
}

/**
 * Set the theme preference in localStorage
 */
export function setStoredTheme(theme: Theme): void {
  localStorage.setItem(STORAGE_KEYS.THEME, theme);
}

/**
 * Save a bid to localStorage
 * CRITICAL: This must be called BEFORE submitting to the blockchain
 */
export function saveBid(data: StoredBid): void {
  const key = `${STORAGE_KEYS.BID_PREFIX}${data.auctionId}`;
  localStorage.setItem(key, JSON.stringify(data));

  // Also update the index of all bids
  const index = getBidIndex();
  if (!index.includes(data.auctionId)) {
    index.push(data.auctionId);
    localStorage.setItem(STORAGE_KEYS.BIDS, JSON.stringify(index));
  }
}

/**
 * Get a bid from localStorage by auction ID
 */
export function getBid(auctionId: string): StoredBid | null {
  const key = `${STORAGE_KEYS.BID_PREFIX}${auctionId}`;
  const data = localStorage.getItem(key);
  if (!data) return null;

  try {
    return JSON.parse(data) as StoredBid;
  } catch {
    return null;
  }
}

/**
 * Get all stored bids
 */
export function getAllBids(): StoredBid[] {
  const index = getBidIndex();
  return index
    .map(auctionId => getBid(auctionId))
    .filter((bid): bid is StoredBid => bid !== null);
}

/**
 * Get the index of all auction IDs with stored bids
 */
function getBidIndex(): string[] {
  const data = localStorage.getItem(STORAGE_KEYS.BIDS);
  if (!data) return [];

  try {
    return JSON.parse(data) as string[];
  } catch {
    return [];
  }
}

/**
 * Mark a bid as revealed
 */
export function markBidRevealed(auctionId: string): void {
  const bid = getBid(auctionId);
  if (bid) {
    saveBid({ ...bid, revealed: true });
  }
}

/**
 * Delete a bid from localStorage
 */
export function deleteBid(auctionId: string): void {
  const key = `${STORAGE_KEYS.BID_PREFIX}${auctionId}`;
  localStorage.removeItem(key);

  const index = getBidIndex();
  const newIndex = index.filter(id => id !== auctionId);
  localStorage.setItem(STORAGE_KEYS.BIDS, JSON.stringify(newIndex));
}

/**
 * Export all bids as a JSON backup
 */
export function exportBackup(): string {
  const bids = getAllBids();
  const backup = {
    version: 1,
    exportedAt: Date.now(),
    bids,
  };
  return JSON.stringify(backup, null, 2);
}

/**
 * Import bids from a JSON backup
 * Returns the number of bids imported
 */
export function importBackup(backupJson: string): number {
  try {
    const backup = JSON.parse(backupJson) as {
      version: number;
      bids: StoredBid[];
    };

    if (!backup.bids || !Array.isArray(backup.bids)) {
      throw new Error('Invalid backup format');
    }

    let imported = 0;
    for (const bid of backup.bids) {
      // Don't overwrite existing bids
      if (!getBid(bid.auctionId)) {
        saveBid(bid);
        imported++;
      }
    }

    return imported;
  } catch (error) {
    console.error('Failed to import backup:', error);
    throw new Error('Invalid backup file');
  }
}

/**
 * Download backup as a file
 */
export function downloadBackup(): void {
  const backup = exportBackup();
  const blob = new Blob([backup], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `vbdesk-backup-${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Get bids that need to be revealed (not yet revealed and auction is in reveal phase)
 */
export function getBidsNeedingReveal(): StoredBid[] {
  return getAllBids().filter(bid => !bid.revealed);
}

/**
 * Check if there's a backup stored
 */
export function hasBackup(): boolean {
  return getAllBids().length > 0;
}
